import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("remembered_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem("remembered_email"));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (remember) localStorage.setItem("remembered_email", email);
      else localStorage.removeItem("remembered_email");
      nav(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO} alt="" className="h-20 w-20 mx-auto mb-4 object-contain" />
          <h1 className="font-serif text-5xl font-light" data-testid="login-title">Bentornato</h1>
          <p className="text-muted-foreground mt-2">Accedi al tuo giardino incantato</p>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div><Label>Email</Label><Input type="email" required name="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" data-testid="login-email" /></div>
          <div><Label>Password</Label>
            <div className="relative mt-2">
              <Input type={showPassword ? "text" : "password"} required name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="pr-11" data-testid="login-password" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold transition-colors" aria-label={showPassword ? "Nascondi password" : "Mostra password"} data-testid="toggle-password-visibility">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="remember-me-label">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-input accent-[hsl(var(--gold))]" data-testid="remember-me-checkbox" />
            Ricorda la mia email su questo dispositivo
          </label>
          {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-gold hover:bg-gold/90 text-white h-12" data-testid="login-submit">
            {loading ? "Accesso…" : "Accedi"}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-8">
          Non hai un account? <Link to="/registrati" className="text-gold hover:underline" data-testid="go-register">Registrati</Link>
        </p>
      </motion.div>
    </div>
  );
}
